import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CreditCard, ExternalLink, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// v2.5.866 — Aligned with LandingPage.jsx tier definitions (Starter/Growth/Enterprise/Agency)
// Features MUST match the landing page's marketing copy so subscribers see the
// exact same value promise inside the SaaS as they saw before signing up.
const plans = [
  {
    id: 'starter', name: 'Starter', price: 299,
    features: ['CRM & Lead Pipeline', 'Basic Analytics Dashboard', 'AI Content (100/mo)', 'Email & Chat Support', 'Website Monitoring', '5 Team Members'],
  },
  {
    id: 'growth', name: 'Growth', price: 699, popular: true,
    features: ['Everything in Starter', 'Advanced Analytics & Reports', 'AI Content (500/mo)', 'Priority Support', 'Full Marketing Suite', 'Helpdesk & SLA Tracking', 'Operations Module', '25 Team Members'],
  },
  {
    id: 'enterprise', name: 'Enterprise', price: null, priceLabel: 'Custom', cta: 'Contact Sales',
    features: ['Everything in Growth', 'Unlimited AI & Team Members', 'White-Label & Multi-Tenant', 'Custom Integrations (Zoho, QuickBooks, etc.)', 'Full Data Migration Support', 'Dedicated Account Manager', 'API Access & Webhooks', 'Data Export & Cloud Backup', 'Custom Compliance & Security'],
  },
  {
    id: 'agency', name: 'Agency', price: 2999,
    features: ['Everything in Enterprise', 'Unlimited Client Sub-Accounts', 'Reseller Dashboard & Roll-up Billing', 'Remove Konnect Branding', 'Priority Support (Dedicated)', '150K monthly AI credits'],
  },
];

export default function BillingTab({ user, subscription, loading, onUpgrade }) {
  const handleManageBilling = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/payments/billing-portal`, { origin_url: window.location.origin });
      if (res.data.url) window.open(res.data.url, '_blank');
    } catch (err) {
      toast.error('Could not open billing portal');
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-white border-gray-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-emerald-brand" />
            Current Plan
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-emerald-brand/5 rounded-lg border border-emerald-brand/20">
            <div>
              <h3 className="text-xl font-heading font-bold text-text-primary capitalize">
                {subscription?.plan_name || user?.plan || 'Starter'}
              </h3>
              <p className="text-gray-medium">${subscription?.price || 299}/month</p>
              {subscription?.renewal_date && (
                <p className="text-sm text-gray-medium mt-1">
                  Renews on {new Date(subscription.renewal_date).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-emerald-brand text-white">Active</Badge>
              <Button variant="outline" size="sm" onClick={handleManageBilling} data-testid="billing-portal-btn">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Manage Billing
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-gray-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Change Your Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((plan) => {
              const isCurrentPlan = user?.plan === plan.id;
              const TIER_ORDER = { starter: 1, growth: 2, enterprise: 3, agency: 4 };
              const currentTier = TIER_ORDER[user?.plan] || 0;
              const planTier = TIER_ORDER[plan.id] || 0;
              const isDowngrade = planTier < currentTier;
              const isEnterpriseCustom = plan.price == null;
              // Enterprise users on the top tier should see "Manage" instead of an empty CTA.
              const actionLabel = isCurrentPlan
                ? (currentTier >= 3 ? 'Current Plan (Top Tier)' : 'Current Plan')
                : isEnterpriseCustom ? (plan.cta || 'Contact Sales')
                : isDowngrade ? 'Downgrade' : 'Upgrade';
              return (
                <div
                  key={plan.id}
                  data-testid={`billing-plan-${plan.id}`}
                  className={cn(
                    'relative border-2 rounded-xl p-5',
                    plan.popular ? 'border-emerald-brand' : 'border-gray-border',
                    isCurrentPlan && 'bg-emerald-brand/5'
                  )}
                >
                  {plan.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-brand text-white text-xs px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  )}
                  <h4 className="font-heading font-semibold text-text-primary">{plan.name}</h4>
                  {isEnterpriseCustom ? (
                    <p className="text-2xl font-bold text-emerald-brand mt-2">{plan.priceLabel || 'Custom'}</p>
                  ) : (
                    <p className="text-2xl font-bold text-emerald-brand mt-2">${plan.price}<span className="text-sm text-gray-medium">/mo</span></p>
                  )}
                  <ul className="mt-4 space-y-2">
                    {plan.features.map((feature, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-gray-medium">
                        <Check className="h-4 w-4 text-emerald-brand flex-shrink-0" />
                        <span className="min-w-0">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={cn(
                      'w-full mt-4',
                      isCurrentPlan ? '' : isDowngrade ? 'bg-gray-500 hover:bg-gray-600' : 'bg-emerald-brand hover:bg-emerald-hover'
                    )}
                    variant={isCurrentPlan ? 'outline' : 'default'}
                    disabled={isCurrentPlan || loading}
                    onClick={() => {
                      if (isEnterpriseCustom && !isCurrentPlan) {
                        window.location.href = 'mailto:sales@kaharian.com?subject=Konnect%20Enterprise%20Inquiry';
                      } else {
                        onUpgrade(plan.id);
                      }
                    }}
                  >
                    {actionLabel}
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
